<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Gestión de Planes y Paquetes</h1>
        <p class="subtitle">Administra planes y paquetes de tokens desde un solo lugar</p>
      </div>
    </div>

    <Card>
      <template #title>
        <div class="section-header">
          <span>Planes</span>
          <Button label="Nuevo plan" icon="pi pi-plus" @click="openCreatePlanDialog" />
        </div>
      </template>
      <template #content>
        <DataTable :value="plans" :loading="loadingPlans" responsiveLayout="scroll" stripedRows>
          <Column field="name" header="Plan" />
          <Column field="monthly_price" header="Precio mensual">
            <template #body="{ data }">
              {{ formatCopFromCents(data.monthly_price) }}
            </template>
          </Column>
          <Column field="included_tokens" header="Tokens incluidos">
            <template #body="{ data }">
              {{ data.included_tokens == null ? 'Ilimitados' : Number(data.included_tokens).toLocaleString('es-CO') }}
            </template>
          </Column>
          <Column field="max_agents" header="Máx. agentes">
            <template #body="{ data }">
              {{ data.max_agents == null ? 'Ilimitados' : data.max_agents }}
            </template>
          </Column>
          <Column field="support_level" header="Soporte" />
          <Column field="is_active" header="Estado">
            <template #body="{ data }">
              <Tag :value="data.is_active ? 'Activo' : 'Inactivo'" :severity="data.is_active ? 'success' : 'secondary'" />
            </template>
          </Column>
          <Column header="Acciones" style="min-width: 180px;">
            <template #body="{ data }">
              <div class="actions">
                <Button icon="pi pi-pencil" text rounded size="small" v-tooltip="'Editar'" @click="openEditPlanDialog(data)" />
                <Button
                  :icon="data.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
                  text
                  rounded
                  size="small"
                  :severity="data.is_active ? 'warning' : 'success'"
                  :v-tooltip="data.is_active ? 'Desactivar' : 'Activar'"
                  @click="togglePlanActive(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Card class="mt-4">
      <template #title>
        <div class="section-header">
          <span>Paquetes de tokens</span>
          <Button label="Nuevo paquete" icon="pi pi-plus" severity="secondary" @click="openCreatePackageDialog" />
        </div>
      </template>
      <template #content>
        <DataTable :value="tokenPackages" :loading="loadingPackages" responsiveLayout="scroll" stripedRows>
          <Column field="name" header="Paquete" />
          <Column field="token_count" header="Tokens">
            <template #body="{ data }">
              {{ Number(data.token_count).toLocaleString('es-CO') }}
            </template>
          </Column>
          <Column field="price_cop" header="Precio (COP)">
            <template #body="{ data }">
              {{ formatCopFromCents(data.price_cop) }}
            </template>
          </Column>
          <Column field="is_active" header="Estado">
            <template #body="{ data }">
              <Tag :value="data.is_active ? 'Activo' : 'Inactivo'" :severity="data.is_active ? 'success' : 'secondary'" />
            </template>
          </Column>
          <Column header="Acciones" style="min-width: 180px;">
            <template #body="{ data }">
              <div class="actions">
                <Button icon="pi pi-pencil" text rounded size="small" v-tooltip="'Editar'" @click="openEditPackageDialog(data)" />
                <Button
                  :icon="data.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
                  text
                  rounded
                  size="small"
                  :severity="data.is_active ? 'warning' : 'success'"
                  :v-tooltip="data.is_active ? 'Desactivar' : 'Activar'"
                  @click="togglePackageActive(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Dialog v-model:visible="showCreatePlanDialog" header="Crear plan" modal :style="{ width: '620px' }">
      <div class="form-grid">
        <div class="field"><label>Nombre</label><InputText v-model="createPlanForm.name" class="w-full" /></div>
        <div class="field"><label>Precio mensual (COP)</label><InputNumber v-model="createPlanForm.monthly_price_cop" class="w-full" :min="0" :useGrouping="true" /></div>
        <div class="field"><label>Tokens incluidos</label><InputNumber v-model="createPlanForm.included_tokens" class="w-full" :min="0" :useGrouping="true" /></div>
        <div class="field"><label>Máx. agentes</label><InputNumber v-model="createPlanForm.max_agents" class="w-full" :min="0" /></div>
        <div class="field"><label>Soporte</label><InputText v-model="createPlanForm.support_level" class="w-full" placeholder="Comunidad / Email / 24/7" /></div>
        <div class="field"><label>Máx. audio (min)</label><InputNumber v-model="createPlanForm.max_audio_minutes" class="w-full" :min="0" /></div>
        <div class="field"><label>Costo extra token (COP)</label><InputNumber v-model="createPlanForm.token_overage_price_cop" class="w-full" :min="0" /></div>
        <div class="field"><label>Costo extra audio/min (COP)</label><InputNumber v-model="createPlanForm.audio_overage_price_cop" class="w-full" :min="0" /></div>
        <div class="field full">
          <label>Proveedores incluidos (separados por coma)</label>
          <InputText v-model="createPlanForm.included_providers_csv" class="w-full" placeholder="mistral, openai, ollama" />
        </div>
      </div>
      <Message v-if="createPlanError" severity="error" :closable="false">{{ createPlanError }}</Message>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showCreatePlanDialog = false" />
        <Button label="Crear" icon="pi pi-check" :loading="savingCreatePlan" @click="createPlan" />
      </template>
    </Dialog>

    <Dialog v-model:visible="showEditPlanDialog" :header="`Editar plan — ${editPlanForm.name || ''}`" modal :style="{ width: '620px' }">
      <div class="form-grid">
        <div class="field"><label>Nombre</label><InputText v-model="editPlanForm.name" class="w-full" /></div>
        <div class="field"><label>Precio mensual (COP)</label><InputNumber v-model="editPlanForm.monthly_price_cop" class="w-full" :min="0" :useGrouping="true" /></div>
        <div class="field"><label>Tokens incluidos</label><InputNumber v-model="editPlanForm.included_tokens" class="w-full" :min="0" :useGrouping="true" /></div>
        <div class="field"><label>Máx. agentes</label><InputNumber v-model="editPlanForm.max_agents" class="w-full" :min="0" /></div>
        <div class="field"><label>Soporte</label><InputText v-model="editPlanForm.support_level" class="w-full" /></div>
        <div class="field"><label>Máx. audio (min)</label><InputNumber v-model="editPlanForm.max_audio_minutes" class="w-full" :min="0" /></div>
        <div class="field"><label>Costo extra token (COP)</label><InputNumber v-model="editPlanForm.token_overage_price_cop" class="w-full" :min="0" /></div>
        <div class="field"><label>Costo extra audio/min (COP)</label><InputNumber v-model="editPlanForm.audio_overage_price_cop" class="w-full" :min="0" /></div>
        <div class="field full">
          <label>Proveedores incluidos (separados por coma)</label>
          <InputText v-model="editPlanForm.included_providers_csv" class="w-full" />
        </div>
      </div>
      <Message v-if="editPlanError" severity="error" :closable="false">{{ editPlanError }}</Message>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showEditPlanDialog = false" />
        <Button label="Guardar" icon="pi pi-check" :loading="savingEditPlan" @click="updatePlan" />
      </template>
    </Dialog>

    <Dialog v-model:visible="showCreatePackageDialog" header="Crear paquete de tokens" modal :style="{ width: '520px' }">
      <div class="form-grid package-grid">
        <div class="field"><label>Nombre</label><InputText v-model="createPackageForm.name" class="w-full" /></div>
        <div class="field"><label>Tokens</label><InputNumber v-model="createPackageForm.token_count" class="w-full" :min="1" :useGrouping="true" /></div>
        <div class="field"><label>Precio (COP)</label><InputNumber v-model="createPackageForm.price_cop_cop" class="w-full" :min="0" :useGrouping="true" /></div>
      </div>
      <Message v-if="createPackageError" severity="error" :closable="false">{{ createPackageError }}</Message>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showCreatePackageDialog = false" />
        <Button label="Crear" icon="pi pi-check" :loading="savingCreatePackage" @click="createPackage" />
      </template>
    </Dialog>

    <Dialog v-model:visible="showEditPackageDialog" :header="`Editar paquete — ${editPackageForm.name || ''}`" modal :style="{ width: '520px' }">
      <div class="form-grid package-grid">
        <div class="field"><label>Nombre</label><InputText v-model="editPackageForm.name" class="w-full" /></div>
        <div class="field"><label>Tokens</label><InputNumber v-model="editPackageForm.token_count" class="w-full" :min="1" :useGrouping="true" /></div>
        <div class="field"><label>Precio (COP)</label><InputNumber v-model="editPackageForm.price_cop_cop" class="w-full" :min="0" :useGrouping="true" /></div>
      </div>
      <Message v-if="editPackageError" severity="error" :closable="false">{{ editPackageError }}</Message>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showEditPackageDialog = false" />
        <Button label="Guardar" icon="pi pi-check" :loading="savingEditPackage" @click="updatePackage" />
      </template>
    </Dialog>

    <Toast />
  </DefaultLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import DefaultLayout from '../../components/layout/DefaultLayout.vue';
import api from '../../services/api';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Message from 'primevue/message';
import Toast from 'primevue/toast';

const toast = useToast();
const loadingPlans = ref(false);
const loadingPackages = ref(false);
const plans = ref([]);
const tokenPackages = ref([]);

const showCreatePlanDialog = ref(false);
const showEditPlanDialog = ref(false);
const savingCreatePlan = ref(false);
const savingEditPlan = ref(false);
const createPlanError = ref('');
const editPlanError = ref('');
const editingPlanId = ref(null);

const showCreatePackageDialog = ref(false);
const showEditPackageDialog = ref(false);
const savingCreatePackage = ref(false);
const savingEditPackage = ref(false);
const createPackageError = ref('');
const editPackageError = ref('');
const editingPackageId = ref(null);

const createPlanForm = ref(getEmptyPlanForm());
const editPlanForm = ref(getEmptyPlanForm());
const createPackageForm = ref(getEmptyPackageForm());
const editPackageForm = ref(getEmptyPackageForm());

function getEmptyPlanForm() {
  return {
    name: '',
    monthly_price_cop: null,
    included_tokens: null,
    max_agents: null,
    support_level: 'Email',
    max_audio_minutes: null,
    token_overage_price_cop: null,
    audio_overage_price_cop: null,
    included_providers_csv: '',
  };
}

function getEmptyPackageForm() {
  return {
    name: '',
    token_count: null,
    price_cop_cop: null,
  };
}

function centsToCop(cents) {
  if (cents == null) return null;
  return Math.round(Number(cents) / 100);
}

function copToCents(cop) {
  if (cop == null || cop === '') return null;
  return Math.round(Number(cop) * 100);
}

function parseProviders(csv) {
  return (csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatCopFromCents(value) {
  const amount = Number(value || 0) / 100;
  return `$${amount.toLocaleString('es-CO')} COP`;
}

async function loadPlans() {
  loadingPlans.value = true;
  try {
    const response = await api.get('/payments/plans/admin');
    plans.value = response.data;
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.detail || 'No se pudo cargar el catálogo de planes',
      life: 5000,
    });
  } finally {
    loadingPlans.value = false;
  }
}

async function loadTokenPackages() {
  loadingPackages.value = true;
  try {
    const response = await api.get('/payments/token-packages/admin');
    tokenPackages.value = response.data;
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.detail || 'No se pudo cargar el catálogo de paquetes',
      life: 5000,
    });
  } finally {
    loadingPackages.value = false;
  }
}

function openCreatePlanDialog() {
  createPlanForm.value = getEmptyPlanForm();
  createPlanError.value = '';
  showCreatePlanDialog.value = true;
}

function openEditPlanDialog(plan) {
  editingPlanId.value = plan.id;
  editPlanError.value = '';
  editPlanForm.value = {
    name: plan.name,
    monthly_price_cop: centsToCop(plan.monthly_price),
    included_tokens: plan.included_tokens,
    max_agents: plan.max_agents,
    support_level: plan.support_level,
    max_audio_minutes: plan.max_audio_minutes,
    token_overage_price_cop: centsToCop(plan.token_overage_price),
    audio_overage_price_cop: centsToCop(plan.audio_overage_price),
    included_providers_csv: (plan.included_providers || []).join(', '),
  };
  showEditPlanDialog.value = true;
}

async function createPlan() {
  if (!createPlanForm.value.name || createPlanForm.value.monthly_price_cop == null || !createPlanForm.value.support_level) {
    createPlanError.value = 'Nombre, precio mensual y nivel de soporte son obligatorios.';
    return;
  }
  savingCreatePlan.value = true;
  createPlanError.value = '';
  try {
    await api.post('/payments/plans', {
      name: createPlanForm.value.name,
      monthly_price: copToCents(createPlanForm.value.monthly_price_cop),
      included_tokens: createPlanForm.value.included_tokens,
      max_agents: createPlanForm.value.max_agents,
      included_providers: parseProviders(createPlanForm.value.included_providers_csv),
      token_overage_price: copToCents(createPlanForm.value.token_overage_price_cop),
      max_audio_minutes: createPlanForm.value.max_audio_minutes,
      audio_overage_price: copToCents(createPlanForm.value.audio_overage_price_cop),
      support_level: createPlanForm.value.support_level,
    });
    showCreatePlanDialog.value = false;
    await loadPlans();
    toast.add({ severity: 'success', summary: 'Plan creado', life: 2500 });
  } catch (error) {
    createPlanError.value = error.response?.data?.detail || 'No se pudo crear el plan';
  } finally {
    savingCreatePlan.value = false;
  }
}

async function updatePlan() {
  if (!editingPlanId.value) return;
  if (!editPlanForm.value.name || editPlanForm.value.monthly_price_cop == null || !editPlanForm.value.support_level) {
    editPlanError.value = 'Nombre, precio mensual y nivel de soporte son obligatorios.';
    return;
  }
  savingEditPlan.value = true;
  editPlanError.value = '';
  try {
    await api.put(`/payments/plans/${editingPlanId.value}`, {
      name: editPlanForm.value.name,
      monthly_price: copToCents(editPlanForm.value.monthly_price_cop),
      included_tokens: editPlanForm.value.included_tokens,
      max_agents: editPlanForm.value.max_agents,
      included_providers: parseProviders(editPlanForm.value.included_providers_csv),
      token_overage_price: copToCents(editPlanForm.value.token_overage_price_cop),
      max_audio_minutes: editPlanForm.value.max_audio_minutes,
      audio_overage_price: copToCents(editPlanForm.value.audio_overage_price_cop),
      support_level: editPlanForm.value.support_level,
    });
    showEditPlanDialog.value = false;
    await loadPlans();
    toast.add({ severity: 'success', summary: 'Plan actualizado', life: 2500 });
  } catch (error) {
    editPlanError.value = error.response?.data?.detail || 'No se pudo actualizar el plan';
  } finally {
    savingEditPlan.value = false;
  }
}

async function togglePlanActive(plan) {
  try {
    await api.put(`/payments/plans/${plan.id}`, { is_active: !plan.is_active });
    await loadPlans();
    toast.add({
      severity: 'success',
      summary: plan.is_active ? 'Plan desactivado' : 'Plan activado',
      life: 2500,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.detail || 'No se pudo cambiar el estado del plan',
      life: 5000,
    });
  }
}

function openCreatePackageDialog() {
  createPackageForm.value = getEmptyPackageForm();
  createPackageError.value = '';
  showCreatePackageDialog.value = true;
}

function openEditPackageDialog(tokenPackage) {
  editingPackageId.value = tokenPackage.id;
  editPackageError.value = '';
  editPackageForm.value = {
    name: tokenPackage.name,
    token_count: tokenPackage.token_count,
    price_cop_cop: centsToCop(tokenPackage.price_cop),
  };
  showEditPackageDialog.value = true;
}

async function createPackage() {
  if (!createPackageForm.value.name || !createPackageForm.value.token_count || createPackageForm.value.price_cop_cop == null) {
    createPackageError.value = 'Nombre, tokens y precio son obligatorios.';
    return;
  }
  savingCreatePackage.value = true;
  createPackageError.value = '';
  try {
    await api.post('/payments/token-packages', {
      name: createPackageForm.value.name,
      token_count: createPackageForm.value.token_count,
      price_cop: copToCents(createPackageForm.value.price_cop_cop),
    });
    showCreatePackageDialog.value = false;
    await loadTokenPackages();
    toast.add({ severity: 'success', summary: 'Paquete creado', life: 2500 });
  } catch (error) {
    createPackageError.value = error.response?.data?.detail || 'No se pudo crear el paquete';
  } finally {
    savingCreatePackage.value = false;
  }
}

async function updatePackage() {
  if (!editingPackageId.value) return;
  if (!editPackageForm.value.name || !editPackageForm.value.token_count || editPackageForm.value.price_cop_cop == null) {
    editPackageError.value = 'Nombre, tokens y precio son obligatorios.';
    return;
  }
  savingEditPackage.value = true;
  editPackageError.value = '';
  try {
    await api.put(`/payments/token-packages/${editingPackageId.value}`, {
      name: editPackageForm.value.name,
      token_count: editPackageForm.value.token_count,
      price_cop: copToCents(editPackageForm.value.price_cop_cop),
    });
    showEditPackageDialog.value = false;
    await loadTokenPackages();
    toast.add({ severity: 'success', summary: 'Paquete actualizado', life: 2500 });
  } catch (error) {
    editPackageError.value = error.response?.data?.detail || 'No se pudo actualizar el paquete';
  } finally {
    savingEditPackage.value = false;
  }
}

async function togglePackageActive(tokenPackage) {
  try {
    await api.put(`/payments/token-packages/${tokenPackage.id}`, { is_active: !tokenPackage.is_active });
    await loadTokenPackages();
    toast.add({
      severity: 'success',
      summary: tokenPackage.is_active ? 'Paquete desactivado' : 'Paquete activado',
      life: 2500,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.detail || 'No se pudo cambiar el estado del paquete',
      life: 5000,
    });
  }
}

onMounted(async () => {
  await Promise.all([loadPlans(), loadTokenPackages()]);
});
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.page-header h1 { margin: 0; color: #1e293b; }
.subtitle { margin: 0.25rem 0 0; color: #6b7280; }
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.actions { display: flex; gap: 0.25rem; }
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
}
.package-grid { grid-template-columns: 1fr; }
.field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.field.full { grid-column: 1 / -1; }
.field label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
}
.w-full { width: 100%; }
.mt-4 { margin-top: 1.5rem; }
</style>
